/**
 * La Tienda Del Padel — Product Comparison Tool
 * Manages compare checkboxes, floating comparison bar, and comparison modal.
 * Stores selections in sessionStorage for persistence during browsing.
 */

class ProductComparison {
  constructor() {
    this.MAX_ITEMS = 3;
    this.STORAGE_KEY = 'lt-compare-products';
    this.bar = document.getElementById('comparison-bar');
    this.modal = document.getElementById('comparison-modal');

    if (!this.bar || !this.modal) return;

    this.products = this.loadFromStorage();
    this.previousActiveElement = null;

    this.init();
  }

  init() {
    this.bindCheckboxes();
    this.bindBarActions();
    this.bindModalActions();
    this.syncCheckboxes();
    this.updateBar();

    // Listen for keydown globally for Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });

    // Re-sync checkbox state whenever new product cards are added to the DOM
    // (collection filter AJAX, lazy-loaded sections, cross-sell carousels, etc.)
    if ('MutationObserver' in window) {
      this.observer = new MutationObserver((mutations) => {
        let hasNewCards = false;
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches?.('.product-card') || node.querySelector?.('.product-card')) {
              hasNewCards = true; break;
            }
          }
          if (hasNewCards) break;
        }
        if (hasNewCards) this.syncCheckboxes();
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    // Also re-sync a few times shortly after load for safety (covers late client hydration)
    setTimeout(() => this.syncCheckboxes(), 500);
    setTimeout(() => this.syncCheckboxes(), 1500);
  }

  /* =============================================
     STORAGE
     ============================================= */

  loadFromStorage() {
    try {
      const data = sessionStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  saveToStorage() {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.products));
    } catch (e) {
      // Silent fail
    }
  }

  /* =============================================
     CHECKBOX HANDLING
     ============================================= */

  bindCheckboxes() {
    document.addEventListener('change', (e) => {
      const checkbox = e.target.closest('[data-compare-toggle]');
      if (!checkbox) return;

      const card = checkbox.closest('.product-card');
      if (!card) return;

      const handle = card.dataset.productHandle;

      if (checkbox.checked) {
        this.addProduct(card);
      } else {
        this.removeProduct(handle);
      }
    });
  }

  syncCheckboxes() {
    const handles = this.products.map(p => p.handle);
    document.querySelectorAll('.product-card').forEach(card => {
      const handle = card.dataset.productHandle;
      const checkbox = card.querySelector('[data-compare-toggle]');
      if (checkbox) {
        checkbox.checked = handles.includes(handle);
      }
    });
  }

  addProduct(card) {
    if (this.products.length >= this.MAX_ITEMS) {
      // Uncheck the box — max reached
      const checkbox = card.querySelector('[data-compare-toggle]');
      if (checkbox) checkbox.checked = false;

      // Brief shake on the compare bar to indicate max
      this.bar.classList.add('is-shake');
      setTimeout(() => this.bar.classList.remove('is-shake'), 500);
      return;
    }

    const product = {
      handle: card.dataset.productHandle || '',
      id: card.dataset.productId || '',
      title: card.dataset.productTitle || '',
      price: card.dataset.productPrice || '',
      comparePrice: card.dataset.productComparePrice || '',
      vendor: card.dataset.productVendor || '',
      image: card.dataset.productImage || '',
      url: card.dataset.productUrl || '',
      variantId: card.dataset.productVariantId || '',
      weight: card.dataset.productWeight || '',
      balance: card.dataset.productBalance || '',
      shape: card.dataset.productShape || '',
      material: card.dataset.productMaterial || '',
      level: card.dataset.productLevel || '',
      style: card.dataset.productStyle || '',
      power: card.dataset.productPower || '',
      control: card.dataset.productControl || '',
      maneuverability: card.dataset.productManeuverability || '',
      sweetSpot: card.dataset.productSweetSpot || '',
      forgiveness: card.dataset.productForgiveness || ''
    };

    // Don't add duplicates
    if (this.products.some(p => p.handle === product.handle)) return;

    this.products.push(product);
    this.saveToStorage();
    this.updateBar();
  }

  removeProduct(handle) {
    this.products = this.products.filter(p => p.handle !== handle);
    this.saveToStorage();
    this.syncCheckboxes();
    this.updateBar();
  }

  clearAll() {
    this.products = [];
    this.saveToStorage();
    this.syncCheckboxes();
    this.updateBar();
  }

  /* =============================================
     FLOATING BAR
     ============================================= */

  updateBar() {
    const productsContainer = this.bar.querySelector('[data-comparison-products]');
    const compareBtn = this.bar.querySelector('[data-comparison-compare]');
    const countEl = this.bar.querySelector('[data-comparison-count]');

    if (this.products.length === 0) {
      this.bar.classList.remove('is-visible');
      return;
    }

    // Render product thumbnails
    productsContainer.innerHTML = this.products.map(product => `
      <div class="comparison-bar__product">
        <img class="comparison-bar__product-image" src="${this.escapeAttr(product.image)}" alt="${this.escapeAttr(product.title)}" width="40" height="40" loading="lazy">
        <span class="comparison-bar__product-name">${this.escapeHtml(product.title)}</span>
        <button class="comparison-bar__product-remove" type="button" data-comparison-remove="${this.escapeAttr(product.handle)}" aria-label="Remove ${this.escapeAttr(product.title)} from comparison">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    `).join('');

    // Update count and button state
    countEl.textContent = this.products.length;
    compareBtn.disabled = this.products.length < 2;

    // Show bar
    this.bar.classList.add('is-visible');

    // Rebind remove buttons
    productsContainer.querySelectorAll('[data-comparison-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.removeProduct(btn.dataset.comparisonRemove);
      });
    });
  }

  bindBarActions() {
    // Compare Now button
    const compareBtn = this.bar.querySelector('[data-comparison-compare]');
    if (compareBtn) {
      compareBtn.addEventListener('click', () => {
        if (this.products.length >= 2) {
          this.openModal();
        }
      });
    }

    // Clear All button
    const clearBtn = this.bar.querySelector('[data-comparison-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.clearAll();
      });
    }
  }

  /* =============================================
     COMPARISON MODAL
     ============================================= */

  openModal() {
    this.previousActiveElement = document.activeElement;
    this.modal.classList.add('is-open');
    document.body.classList.add('no-scroll');

    // Render modal content
    this.renderModal();

    // Focus close button
    requestAnimationFrame(() => {
      const closeBtn = this.modal.querySelector('.comparison-modal__close');
      if (closeBtn) closeBtn.focus();
    });
  }

  closeModal() {
    this.modal.classList.remove('is-open');
    document.body.classList.remove('no-scroll');

    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus();
    }
  }

  bindModalActions() {
    // Close button
    const closeBtn = this.modal.querySelector('[data-comparison-modal-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeModal());
    }

    // Overlay click
    const overlay = this.modal.querySelector('.comparison-modal__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closeModal());
    }
  }

  renderModal() {
    const body = this.modal.querySelector('.comparison-modal__body');
    if (!body) return;

    if (this.products.length < 2) {
      body.innerHTML = '<div class="comparison-modal__loading">Select at least 2 products to compare.</div>';
      return;
    }

    const specs = [
      { key: 'vendor', label: 'Brand' },
      { key: 'price', label: 'Price', format: 'money' },
      { key: 'level', label: 'Player Level' },
      { key: 'shape', label: 'Shape' },
      { key: 'style', label: 'Play Style' },
      { key: 'power', label: 'Power', format: 'bar' },
      { key: 'control', label: 'Control', format: 'bar' },
      { key: 'maneuverability', label: 'Maneuverability', format: 'bar' },
      { key: 'sweetSpot', label: 'Sweet Spot', format: 'bar' },
      { key: 'forgiveness', label: 'Forgiveness', format: 'bar' },
      { key: 'weight', label: 'Weight' },
      { key: 'balance', label: 'Balance' },
      { key: 'material', label: 'Material' }
    ];

    // Hide specs where every product is empty (avoids rows of all "—")
    // Always keep core specs that should be visible even if empty.
    const alwaysShow = new Set(['vendor', 'price']);
    const visibleSpecs = specs.filter(spec => {
      if (alwaysShow.has(spec.key)) return true;
      return this.products.some(p => {
        const raw = (p[spec.key] || '').toString().trim();
        return raw !== '' && raw !== '0';
      });
    });

    // Find which values differ to highlight
    const diffKeys = new Set();
    visibleSpecs.forEach(spec => {
      const values = this.products.map(p => (p[spec.key] || '').toString().trim().toLowerCase());
      const unique = new Set(values.filter(v => v !== ''));
      if (unique.size > 1) {
        diffKeys.add(spec.key);
      }
    });

    const colCount = this.products.length;
    const colWidth = colCount === 2 ? '40%' : '27%';

    let html = '<table class="comparison-modal__table">';

    // Header row: product images, titles, prices
    html += '<thead><tr>';
    html += '<th class="comparison-modal__label" style="border-bottom: 1px solid rgba(255,255,255,0.08);"></th>';
    this.products.forEach(product => {
      html += `
        <th class="comparison-modal__product-header" style="width:${colWidth}">
          <button class="comparison-modal__product-remove" type="button" data-modal-remove="${this.escapeAttr(product.handle)}" aria-label="Remove ${this.escapeAttr(product.title)}">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          ${product.image ? `<img class="comparison-modal__product-image" src="${this.escapeAttr(product.image)}" alt="${this.escapeAttr(product.title)}" width="120" height="120" loading="lazy">` : ''}
          <div class="comparison-modal__product-title">${this.escapeHtml(product.title)}</div>
          <div class="comparison-modal__product-price">${this.formatMoney(product.price)}</div>
          <a href="${this.escapeAttr(product.url)}" class="comparison-modal__product-link">View Product &rarr;</a>
        </th>
      `;
    });
    html += '</tr></thead>';

    // Spec rows
    html += '<tbody>';
    visibleSpecs.forEach(spec => {
      html += '<tr>';
      html += `<td class="comparison-modal__label">${this.escapeHtml(spec.label)}</td>`;
      this.products.forEach(product => {
        const raw = (product[spec.key] || '').toString().trim();
        const isEmpty = raw === '' || raw === '0';
        let rendered = '';

        if (spec.format === 'money') {
          rendered = this.escapeHtml(this.formatMoney(product[spec.key]));
        } else if (spec.format === 'bar') {
          const pct = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
          if (pct > 0) {
            rendered = `
              <div class="comparison-modal__bar" aria-label="${spec.label}: ${pct} out of 100">
                <div class="comparison-modal__bar-fill" style="width:${pct}%"></div>
                <span class="comparison-modal__bar-value">${pct}</span>
              </div>`;
          }
        } else {
          rendered = this.escapeHtml(raw);
        }

        const isHighlight = diffKeys.has(spec.key) && !isEmpty;
        const className = isEmpty
          ? 'comparison-modal__value comparison-modal__value--empty'
          : isHighlight
            ? 'comparison-modal__value comparison-modal__value--highlight'
            : 'comparison-modal__value';
        html += `<td class="${className}">${isEmpty ? '&mdash;' : rendered}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody>';

    // Add to Cart row
    html += '<tfoot><tr>';
    html += '<td class="comparison-modal__cart-cell"></td>';
    this.products.forEach(product => {
      html += `
        <td class="comparison-modal__cart-cell">
          <button class="comparison-modal__add-btn" type="button" data-modal-add-to-cart="${this.escapeAttr(product.variantId)}">
            Add to Cart
          </button>
        </td>
      `;
    });
    html += '</tr></tfoot>';

    html += '</table>';
    body.innerHTML = html;

    // Bind remove buttons inside modal
    body.querySelectorAll('[data-modal-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.removeProduct(btn.dataset.modalRemove);
        if (this.products.length < 2) {
          this.closeModal();
        } else {
          this.renderModal();
        }
      });
    });

    // Bind Add to Cart buttons inside modal
    body.querySelectorAll('[data-modal-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.modalAddToCart;
        if (!variantId) return;

        btn.disabled = true;
        btn.textContent = 'Adding...';

        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            btn.textContent = 'Added!';
            btn.style.background = '#0cc45f';

            // Refresh the drawer (line items + header count + recs) and open it.
            // Helper defined in cart-drawer.js.
            window.refreshCartDrawer();

            setTimeout(() => {
              btn.textContent = 'Add to Cart';
              btn.style.background = '';
              btn.disabled = false;
            }, 1500);
          } else {
            btn.textContent = 'Error';
            setTimeout(() => {
              btn.textContent = 'Add to Cart';
              btn.disabled = false;
            }, 1500);
          }
        } catch (err) {
          console.error('Comparison add to cart error:', err);
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
        }
      });
    });
  }

  /* =============================================
     UTILITY FUNCTIONS
     ============================================= */

  formatMoney(cents) {
    if (!cents || cents === '') return '';
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2);
    const formatted = parseFloat(amount).toLocaleString();
    return `${this.getCurrencySymbol()}${formatted}`;
  }

  getCurrencySymbol() {
    const existing = document.querySelector('.price__regular, .price__sale');
    if (existing) {
      const text = existing.textContent.trim();
      const match = text.match(/^([^\d]+)/);
      if (match) return match[1];
    }
    return 'LE ';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  escapeAttr(text) {
    return (text || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.productComparison = new ProductComparison();
});
